import GameState from "./game-state";
import { IGame } from "../types/game";
import { KeyStatus, CanvasStatus } from "../types/stats";
import PlayerAddBonusState from "./add-bonus-state";

export default class DeadWaitState extends GameState {
  private lastKeyStates: { [key: string]: KeyStatus } = {};

  constructor(game: IGame) {
    super(game);
    // Match original Player_dead_wait constructor (player.cc:973-978)
    // Original: sets idle=2, state=WAITFORWINNER
    this.canvas.idle = 2;
    this.canvas.status = CanvasStatus.WAITFORWINNER;
  }

  enter(): void {
    // Match original Player_dead_wait constructor (player.cc:973-978)
    // Original: sets idle=2, state=WAITFORWINNER, clears keys
    this.canvas.idle = 2;
    this.canvas.status = CanvasStatus.WAITFORWINNER;
    if (this.game.getGameMode() === "survivor" && this.game.sendClientState) {
      this.game.sendClientState("waitForWinner");
    }

    // Clear key states when entering dead wait (matches original C++ clear_key_all)
    if (this.game.input) {
      this.lastKeyStates = {
        keyLeft: this.game.input.keyLeft,
        keyRight: this.game.input.keyRight,
        keyDown: this.game.input.keyDown,
        keyRotate: this.game.input.keyRotate,
        keyRotateTwice: this.game.input.keyRotateTwice,
        keyDrop: this.game.input.keyDrop,
      };
      // Reset all keys to NONE to detect new presses
      this.game.input.keyLeft = KeyStatus.NONE;
      this.game.input.keyRight = KeyStatus.NONE;
      this.game.input.keyDown = KeyStatus.NONE;
      this.game.input.keyRotate = KeyStatus.NONE;
      this.game.input.keyRotateTwice = KeyStatus.NONE;
      this.game.input.keyDrop = KeyStatus.NONE;
    }

    // Match original Player_dead_wait::init() (player.cc:980-988)
    // Original: sets message, doesn't check syncpoint, doesn't send P_CLIENTSTATE
    // In the original, the server has direct access to canvas->state for all players.
    // In our implementation, the server already sets canvasState: "waitForWinner" when players join,
    // so we don't need to send it here. We only send P_CLIENTSTATE when transitioning states
    // (WAITFORWINNER → WAITFORRESTART), which is done in update() when syncpoint changes.
  }

  update(frameTime: number): void {
    super.update(frameTime);

    // NOTE: P_RESPAWN check removed - that's handled by WaitRespawnState for remote players
    // DeadWaitState is ONLY for local players (matches original Player_dead_wait)

    const gameMode = this.game.getGameMode();
    const isSurvivorMode = gameMode === "survivor";
    const syncpoint = this.game.getSyncpoint();

    // In standard/FFA mode (no syncpoint system), require key press to respawn
    // In survivor mode, wait for syncpoint transitions
    if (!isSurvivorMode && this.game.input) {
      // Check for any NEW key press to respawn (matches original C++ behavior)
      // Only respawn if a key was just pressed (not if it was already pressed)
      const input = this.game.input;
      const anyKeyPressed =
        (input.keyLeft === KeyStatus.PRESSED && this.lastKeyStates.keyLeft !== KeyStatus.PRESSED) ||
        (input.keyRight === KeyStatus.PRESSED && this.lastKeyStates.keyRight !== KeyStatus.PRESSED) ||
        (input.keyDown === KeyStatus.PRESSED && this.lastKeyStates.keyDown !== KeyStatus.PRESSED) ||
        (input.keyRotate === KeyStatus.PRESSED && this.lastKeyStates.keyRotate !== KeyStatus.PRESSED) ||
        (input.keyRotateTwice === KeyStatus.PRESSED && this.lastKeyStates.keyRotateTwice !== KeyStatus.PRESSED) ||
        (input.keyDrop === KeyStatus.PRESSED && this.lastKeyStates.keyDrop !== KeyStatus.PRESSED);

      // Update last key states
      this.lastKeyStates = {
        keyLeft: input.keyLeft,
        keyRight: input.keyRight,
        keyDown: input.keyDown,
        keyRotate: input.keyRotate,
        keyRotateTwice: input.keyRotateTwice,
        keyDrop: input.keyDrop,
      };

      if (anyKeyPressed) {
        // Never restart in single-player games
        // Reference: quadra/source/player.cc:1021-1023
        // Original: if(game->single) restart=false;
        if ((this.game as any).single) {
          // In single player, game has ended, don't allow restart
          return;
        }
        
        // Clear ALL key states before respawning so keys don't affect game after respawn
        // (matches original C++ behavior where canvas->restart() clears keys)
        input.keyLeft = KeyStatus.NONE;
        input.keyRight = KeyStatus.NONE;
        input.keyDown = KeyStatus.NONE;
        input.keyRotate = KeyStatus.NONE;
        input.keyRotateTwice = KeyStatus.NONE;
        input.keyDrop = KeyStatus.NONE;
        
        // Update last key states to reflect cleared state
        this.lastKeyStates = {
          keyLeft: KeyStatus.NONE,
          keyRight: KeyStatus.NONE,
          keyDown: KeyStatus.NONE,
          keyRotate: KeyStatus.NONE,
          keyRotateTwice: KeyStatus.NONE,
          keyDrop: KeyStatus.NONE,
        };
        
        // Respawn immediately in standard/FFA mode on key press
        this.game.restartPlayer?.();
        this.game.popState(this);
        return;
      } else {
        // Check for bonus while waiting (like original Player_dead_wait::step() line 1036 else branch)
        // Original: else { if(canvas->bonus && !canvas->bon[0].blind_time && add_bonus) call(new Player_add_bonus(canvas)); }
        if (this.canvas.bonus > 0 && this.canvas.bon[0] && !this.canvas.bon[0].blind_time) {
          this.game.pushState(new PlayerAddBonusState(this.game));
        }
      }
    }

    // Only check syncpoint in survivor mode
    if (isSurvivorMode) {
      // Match original Player_dead_wait::check_first_frag() (player.cc:1041-1060)
      // Original checks syncpoint transitions, NOT packets
      // FirstFragPacket is handled in WaitBlockState (matches Player_wait_block::check_first_frag)
      
      // Debug: log syncpoint check occasionally
      if (Math.random() < 0.01) { // 1% of the time
        console.log(`[DeadWaitState] Checking syncpoint: canvas.status=${this.canvas.status}, syncpoint=${syncpoint}`);
      }
      
      // Original uses switch on canvas->state, checks syncpoint transitions
      switch (this.canvas.status) {
        case CanvasStatus.WAITFORWINNER:
          if (syncpoint === "waitForRestart") {
            console.log(`[DeadWaitState] ✅ Transitioning WAITFORWINNER → WAITFORRESTART (syncpoint=${syncpoint})`);
            this.canvas.status = CanvasStatus.WAITFORRESTART;
            this.game.sendClientState("waitForRestart");
          } else if (syncpoint) {
            // Debug: log when syncpoint exists but doesn't match
            if (Math.random() < 0.1) { // 10% of the time
              console.log(`[DeadWaitState] Waiting for waitForRestart, current syncpoint=${syncpoint}`);
            }
          }
          break;

        case CanvasStatus.WAITFORRESTART:
          if (syncpoint === "playing") {
            // check_first_frag() returns true, which triggers restart in step()
            // Original: if(restart) { canvas->restart(); ret(); }
            this.game.restartPlayer?.();
            
            // Set canvas status back to PLAYING (matches Canvas::restart() line 281)
            this.canvas.status = CanvasStatus.PLAYING;
            
            // Inform server that we're back in PLAYING state
            // This allows server to detect when all players are PLAYING and transition syncpoint to LAST
            if (this.game.sendClientState) {
              this.game.sendClientState("playing");
            }
            
            this.game.popState(this);
            return; // Early return after restart
          }
          break;

        case CanvasStatus.PLAYING:
        case CanvasStatus.LAST:
          // At game start, when syncpoint transitions from "last" to "playing", pop DeadWaitState
          // This matches original behavior where Player_dead_wait exits when game actually starts
          if (syncpoint === "playing") {
            this.game.popState(this);
            return;
          }
          // Otherwise, no action needed (waiting for syncpoint to change)
          break;
      }

      // Check for bonus while waiting (like original Player_dead_wait::step() else branch)
      // Original: else { if(canvas->bonus && !canvas->bon[0].blind_time && add_bonus) call(new Player_add_bonus(canvas)); }
      if (this.canvas.bonus > 0 && this.canvas.bon[0] && !this.canvas.bon[0].blind_time) {
        this.game.pushState(new PlayerAddBonusState(this.game));
      }
    }
  }


  toString(): string {
    return "Game Over Wait";
  }
}
